import { create } from "zustand";

import * as experimentService from "@/services/experimentService";
import * as insightService from "@/services/insightService";
import { getErrorMessage } from "@/types/problem-detail";

type InsightRow = Awaited<ReturnType<typeof insightService.fetchInsights>>[number];
type ExperimentRow = Awaited<ReturnType<typeof experimentService.fetchExperiments>>[number];

interface LaunchContextCatalogEntry {
  insights: InsightRow[];
  experiments: ExperimentRow[];
  loading: boolean;
  error: string | null;
  loadedAt: number | null;
  _inFlight?: Promise<void>;
}

const CACHE_TTL_MS = 30_000;

const emptyEntry = (): LaunchContextCatalogEntry => ({
  insights: [],
  experiments: [],
  loading: false,
  error: null,
  loadedAt: null,
});

const isCacheFresh = (loadedAt: number | null): boolean => {
  if (loadedAt == null) return false;
  return Date.now() - loadedAt < CACHE_TTL_MS;
};

interface ProjectLaunchContextCatalogStore {
  byProject: Record<string, LaunchContextCatalogEntry>;
  load: (projectId: string, options?: { force?: boolean }) => Promise<void>;
  invalidate: (projectId: string) => void;
}

export const useProjectLaunchContextCatalogStore = create<ProjectLaunchContextCatalogStore>(
  (set, get) => ({
    byProject: {},

    load: async (projectId, options) => {
      const entry = get().byProject[projectId] ?? emptyEntry();
      if (!options?.force && isCacheFresh(entry.loadedAt) && !entry.error) {
        return;
      }
      if (entry._inFlight) {
        await entry._inFlight;
        if (!options?.force) return;
        const after = get().byProject[projectId];
        if (after?._inFlight) {
          await after._inFlight;
          return;
        }
      }

      const promise = (async () => {
        set((state) => ({
          byProject: {
            ...state.byProject,
            [projectId]: {
              ...(state.byProject[projectId] ?? emptyEntry()),
              loading: true,
              error: null,
            },
          },
        }));

        try {
          const [insights, experiments] = await Promise.all([
            insightService.fetchInsights(projectId),
            experimentService.fetchExperiments(projectId),
          ]);
          set((state) => ({
            byProject: {
              ...state.byProject,
              [projectId]: {
                insights,
                experiments,
                loading: false,
                error: null,
                loadedAt: Date.now(),
              },
            },
          }));
        } catch (error: unknown) {
          const message = getErrorMessage(error, "Failed to load project sources");
          set((state) => ({
            byProject: {
              ...state.byProject,
              [projectId]: {
                ...(state.byProject[projectId] ?? emptyEntry()),
                loading: false,
                error: message,
              },
            },
          }));
        } finally {
          set((state) => {
            const cur = state.byProject[projectId];
            if (!cur) return state;
            const { _inFlight: _omit, ...rest } = cur;
            void _omit;
            return {
              byProject: {
                ...state.byProject,
                [projectId]: rest,
              },
            };
          });
        }
      })();

      set((state) => ({
        byProject: {
          ...state.byProject,
          [projectId]: {
            ...(state.byProject[projectId] ?? emptyEntry()),
            _inFlight: promise,
          },
        },
      }));

      await promise;
    },

    invalidate: (projectId) => {
      set((state) => {
        if (!state.byProject[projectId]) return state;
        return {
          byProject: {
            ...state.byProject,
            [projectId]: emptyEntry(),
          },
        };
      });
    },
  }),
);
